// UI - HUD, menus, draft shop, and game over
// Manages all user interface screens: main menu, in-game HUD, draft card pick, game over.

import { CONST, pointInRect } from './utils.js';
import { getBestScore, getBestWave, getUnlockedRelics, getActiveRelics, setRelicActive } from './storage.js';
import { RELICS, getRelic } from './relics.js';
import { BUFF_DISPLAY } from './pickups.js';
import { randFn } from './rng.js';
import { t, getLang } from './i18n.js';
import { isTelemetryOptedOut } from './session-logger.js';

// Menu lettering (Renderer.pixelText). Same palette family as the splash
// title: gold bands, brown drop shadow, near-black outline, orange glow.
const MENU_TITLE_STYLE = {
  key: 'menu-title',
  family: 'monospace',
  sizeCells: 32,
  bands: ['#ffe866', '#ffd700', '#ffc400', '#f0a800'],
  shadow: '#4a2a00',
  shadowCells: 2,
  outline: '#140a00',
  glow: '#ffaa00',
  glowCells: 2,
};
const MENU_SUBTITLE_STYLE = {
  key: 'menu-subtitle',
  family: 'monospace',
  sizeCells: 12,
  bands: ['#f0d488', '#e8c872'],
  outline: '#140a00',
};

// Menu control hints. Each hint string is « • »-separated parts; parts that
// don't fit one row move to rows of their own.
const MENU_HINT_MIN_SIZE = 11;
const MENU_HINT_SEP = '   •   ';
const MENU_HINT_GAP = 9;      // between hint strings (the desktop spacing)
const MENU_HINT_ROW_GAP = 4;  // between rows of one wrapped string

// Card rarities drive deck composition and card border colors. LEGENDARY
// is reserved for the rebirth sentinel that lives at the bottom of the deck.
const Rarity = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  LEGENDARY: 'legendary',
};

// Target number of physical cards per rarity in the run-start deck. Sums to
// 40, shaped 60/30/10 — the same distribution the old weighted roll
// approximated. Unstackable cards always claim one slot of their rarity's
// quota; remaining slots are spread as evenly as possible across stackable
// cards of that rarity (extras go to earlier ids in CARDS order).
// v1.3.0: uncommon raised 12 → 18 to accommodate three new uncommon
// cards (fortify, armorBreak, cleave) without shrinking copies of the
// existing five — keeps each uncommon at ~2 copies. Rare raised 4 → 6
// for crit + phantom; with one more non-stackable (phantom) and one new
// stackable (crit) alongside existing glassCannon (stackable) + wideArc
// (non-stackable), stackable rares get ~2 copies each.
// v1.3.x: uncommon 18 → 20 for doubleStrike — keeps the stackable
// uncommons at ~2 copies each (9 stackable + armorBreak non-stack).
const DECK_TARGET = {
  [Rarity.COMMON]: 24,
  [Rarity.UNCOMMON]: 20,
  [Rarity.RARE]: 6,
};

const RARITY_COLOR = {
  [Rarity.COMMON]: '#8aa3a8',
  [Rarity.UNCOMMON]: '#4a90e2',
  [Rarity.RARE]: '#e2b44a',
  [Rarity.LEGENDARY]: '#ffd700',
};

// Gold cost per rarity. Both interwave (end-of-wave) and shop (walk-in)
// drafts charge the same price — there is no "free" draft mode. Legendary
// (only used by the rebirth sentinel) is intentionally free: the card's
// trade-off is the run reset itself, not gold.
const RARITY_COST = {
  [Rarity.COMMON]: 15,
  [Rarity.UNCOMMON]: 40,
  [Rarity.RARE]: 80,
  [Rarity.LEGENDARY]: 0,
};

// Diminishing returns on stacked cards. Index = how many copies the player
// already owns BEFORE the current pick; 0 = first stack (full effect).
// Hard cap past index 4 so nth-copy picks still feel non-zero.
const STACK_SCALE = [1, 0.6, 0.3, 0.15, 0.1];
export function stackScale(stackIndex) {
  const i = Math.max(0, stackIndex | 0);
  return STACK_SCALE[Math.min(i, STACK_SCALE.length - 1)];
}

// Compact number formatter for card descriptions. Rounds to 2 decimals
// then strips trailing zeros so "+5 damage" stays "+5" but a stacked
// "+0.05s i-frame" keeps full precision instead of rounding to "+0.1s".
function fmtN(v) {
  return parseFloat(v.toFixed(2)).toString();
}

// Card pool. Each card has an apply(player, scale) that mutates player
// stats. `scale` comes from stackScale(ownedCountBeforePick); cards that
// care about stacking multiply their effect by it (additive stats trivially,
// multiplicative stats via (1 − base * scale)). `stackable: false` cards
// drop out of the pool once taken.
export const CARDS = [
  // --- Stat cards (common, multi-take) ---
  {
    id: 'damage',
    get label() { return t('card.damage.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.damage.desc', { value: fmtN(5 * scale) }); },
    icon: '⚔',
    rarity: Rarity.COMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.damage += 5 * scale; },
  },
  {
    id: 'maxHp',
    get label() { return t('card.maxHp.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.maxHp.desc', { value: Math.round(25 * scale) }); },
    icon: '❤',
    rarity: Rarity.COMMON,
    stackable: true,
    apply: (p, scale = 1) => {
      const bonus = Math.round(25 * scale);
      p.maxHp += bonus;
      // Fully restore HP — matches the "and healing" promise on the
      // card. Without this a full-HP buyer sees no immediate HP change.
      p.hp = p.maxHp;
    },
  },
  {
    id: 'speed',
    get label() { return t('card.speed.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) {
      return t('card.speed.desc', { speed: fmtN(15 * scale), cd: fmtN(6 * scale) });
    },
    icon: '👢',
    rarity: Rarity.COMMON,
    stackable: true,
    apply: (p, scale = 1) => {
      p.speed += 15 * scale;
      p.attackCooldown = Math.max(0.12, p.attackCooldown * (1 - 0.06 * scale));
    },
  },
  {
    id: 'attackRange',
    get label() { return t('card.attackRange.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.attackRange.desc', { value: fmtN(2 * scale) }); },
    icon: '🗡',
    rarity: Rarity.COMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.attackRange += 2 * scale; },
  },
  {
    id: 'cooldown',
    get label() { return t('card.cooldown.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.cooldown.desc', { value: fmtN(12 * scale) }); },
    icon: '⏱',
    rarity: Rarity.COMMON,
    stackable: true,
    apply: (p, scale = 1) => {
      p.attackCooldown = Math.max(0.12, p.attackCooldown * (1 - 0.12 * scale));
    },
  },
  // --- Mechanical cards (uncommon) ---
  {
    id: 'lifesteal',
    get label() { return t('card.lifesteal.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.lifesteal.desc', { value: fmtN(7 * scale) }); },
    icon: '🩸',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.lifestealPct += 0.07 * scale; },
  },
  {
    id: 'magnet',
    get label() { return t('card.magnet.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.magnet.desc', { value: fmtN(50 * scale) }); },
    icon: '🧲',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.magnetRangeMul += 0.5 * scale; },
  },
  {
    id: 'thorns',
    get label() { return t('card.thorns.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.thorns.desc', { value: fmtN(25 * scale) }); },
    icon: '🌵',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.thornsPct += 0.25 * scale; },
  },
  {
    id: 'regen',
    get label() { return t('card.regen.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.regen.desc', { value: fmtN(1.85 * scale) }); },
    icon: '💚',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    // Base regen is 0, so this card carries the whole sustain budget.
    // 1.85/copy × STACK_SCALE sums to ~3.98 over the first 5 copies.
    apply: (p, scale = 1) => { p.regenPerSec += 1.85 * scale; },
  },
  {
    id: 'dashCooldown',
    get label() { return t('card.dashCooldown.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) {
      return t('card.dashCooldown.desc', { cd: fmtN(25 * scale), iframe: fmtN(0.05 * scale) });
    },
    icon: '⚡',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => {
      p.dashCooldownMax = Math.max(0.1, p.dashCooldownMax * (1 - 0.25 * scale));
      p.iframeBonus += 0.05 * scale;
    },
  },
  // Effect applied via UI special-case in pickCard, not here. Kept as
  // a no-op apply so the rest of the drafting machinery treats it like
  // any other card (cost, stack count, exclusion).
  {
    id: 'draftSize',
    get label() { return t('card.draftSize.label'); },
    get desc() { return t('card.draftSize.desc'); },
    icon: '📜',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: () => {},
  },
  // --- v1.3.0 new uncommons ---
  // fortify: passive heal-on-kill. Combat code reads
  // player.fortifyHealPerKill in performAttack when a guard dies.
  {
    id: 'fortify',
    get label() { return t('card.fortify.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.fortify.desc', { value: Math.round(3 * scale) }); },
    icon: '🛡',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.fortifyHealPerKill += 3 * scale; },
  },
  // armorBreak: non-stackable. combat.calcDamage skips armor reduction
  // for the attacker when player.armorBreak === true. Direct counter to
  // the armored guards that account for ~75% of W4-W8 first-time deaths.
  {
    id: 'armorBreak',
    get label() { return t('card.armorBreak.label'); },
    get desc() { return t('card.armorBreak.desc'); },
    icon: '🪓',
    rarity: Rarity.UNCOMMON,
    stackable: false,
    apply: (p) => { p.armorBreak = true; },
  },
  // cleave: secondary damage to all other guards in attack arc. combat
  // applies player.cleavePct to non-primary hits after primary damage.
  {
    id: 'cleave',
    get label() { return t('card.cleave.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.cleave.desc', { value: Math.round(35 * scale) }); },
    icon: '⚔',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p, scale = 1) => { p.cleavePct = Math.min(0.95, p.cleavePct + 0.35 * scale); },
  },
  // doubleStrike (rhythm): every Nth swing has half cooldown. Stacks
  // unlock additional Ns from the [5, 7, 11] schedule — 1st copy adds
  // 5, 2nd adds 7, 3rd adds 11. apply() appends the next trigger based
  // on current array length so the stackScale value (which doesn't
  // carry the discrete stack index for a per-pick increment like this)
  // is ignored. Hard cap at 3 copies: further picks are no-ops.
  // descAt receives ownedNow so the card preview can show the trigger
  // THIS PICK will add, not a meaningless scaled-down stacked value.
  {
    id: 'doubleStrike',
    get label() { return t('card.doubleStrike.label'); },
    get desc() { return this.descAt(1, 0); },
    descAt(_scale, ownedNow = 0) {
      const ns = [5, 7, 11];
      if (ownedNow >= ns.length) return t('card.doubleStrike.descFull');
      return t('card.doubleStrike.desc', { n: ns[ownedNow] });
    },
    icon: '⚔',
    rarity: Rarity.UNCOMMON,
    stackable: true,
    apply: (p) => {
      const ns = [5, 7, 11];
      const next = ns[p.fastAttackTriggers.length];
      if (next != null) p.fastAttackTriggers.push(next);
    },
  },
  // --- Rare cards ---
  // crit: stackable chance of doubled damage. combat rolls
  // Math.random() < player.critChance per hit. Cap at 60% so a
  // stack-3 build still has a miss chance.
  {
    id: 'crit',
    get label() { return t('card.crit.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) { return t('card.crit.desc', { value: Math.round(20 * scale) }); },
    icon: '🎯',
    rarity: Rarity.RARE,
    stackable: true,
    apply: (p, scale = 1) => {
      p.critChance = Math.min(0.6, p.critChance + 0.20 * scale);
    },
  },
  // phantom: non-stackable. Overrides dash iframe duration so player
  // is invulnerable for the full second after dashing. Pairs with
  // dashCooldown for a high-mobility dodge build.
  {
    id: 'phantom',
    get label() { return t('card.phantom.label'); },
    get desc() { return t('card.phantom.desc'); },
    icon: '👻',
    rarity: Rarity.RARE,
    stackable: false,
    apply: (p) => { p.dashIframeOverride = 1.0; },
  },
  {
    id: 'glassCannon',
    get label() { return t('card.glassCannon.label'); },
    get desc() { return this.descAt(1); },
    descAt(scale) {
      return t('card.glassCannon.desc', { dmg: fmtN(10 * scale), hp: Math.round(15 * scale) });
    },
    icon: '💀',
    rarity: Rarity.RARE,
    stackable: true,
    apply: (p, scale = 1) => {
      p.damage += 10 * scale;
      p.maxHp = Math.max(10, p.maxHp - Math.round(15 * scale));
      p.hp = Math.min(p.hp, p.maxHp);
    },
  },
  {
    id: 'wideArc',
    get label() { return t('card.wideArc.label'); },
    get desc() { return t('card.wideArc.desc'); },
    icon: '🌀',
    rarity: Rarity.RARE,
    stackable: false,
    apply: (p) => { p.fullArcAttack = true; },
  },
  // --- Legendary sentinel (bottom of the deck) ---
  // Picking rebirth doesn't mutate the player directly — pickCard sets
  // `rebirthPending`, which the Game polls to drive the cutscene and run
  // reset. The deck builder appends exactly one copy at the end; the
  // draw logic defers it until every other card has been consumed.
  {
    id: 'rebirth',
    get label() { return t('card.rebirth.label'); },
    get desc() { return t('card.rebirth.desc'); },
    icon: '✨',
    rarity: Rarity.LEGENDARY,
    stackable: false,
    apply: () => {},
  },
];

// Build the run-start deck: a multiset of card ids drawn uniformly at random
// over the run. Rarity bias is baked into the multiplicity (DECK_TARGET);
// the deck is consumed by picks — there is no refill until a new run.
// One copy of the rebirth sentinel is appended at the end; rollFromDeck
// keeps it out of every draw until everything else has been consumed.
export function buildDeck() {
  const deck = [];
  const byRarity = {};
  for (const c of CARDS) (byRarity[c.rarity] ||= []).push(c);
  for (const rarity of Object.keys(DECK_TARGET)) {
    const cards = byRarity[rarity] || [];
    const target = DECK_TARGET[rarity];
    const unstackable = cards.filter(c => !c.stackable);
    const stackable = cards.filter(c => c.stackable);
    for (const c of unstackable) deck.push(c.id);
    const remaining = Math.max(0, target - unstackable.length);
    if (stackable.length === 0) continue;
    const base = Math.floor(remaining / stackable.length);
    let extra = remaining % stackable.length;
    for (const c of stackable) {
      const copies = base + (extra > 0 ? 1 : 0);
      if (extra > 0) extra--;
      for (let i = 0; i < copies; i++) deck.push(c.id);
    }
  }
  // Legendary sentinel: exactly one rebirth at the bottom.
  for (const c of CARDS) {
    if (c.rarity === Rarity.LEGENDARY) deck.push(c.id);
  }
  return deck;
}

// Draw one card uniformly at random from `deck`, skipping ids in `exclude`
// (already-drawn in this offer) and unstackable ids the player already owns.
// The rebirth sentinel is held back until every non-rebirth card has left
// the deck — surfacing it earlier would break the "bottom of the deck"
// promise. On success the chosen card is spliced out of `deck` and the
// card definition is returned. `rng` is optional — sims pass a seeded
// generator; the live game falls back to Math.random.
function rollFromDeck(deck, exclude, ownedUnstackable, rng) {
  if (deck.length === 0) return null;
  const rand = randFn(rng);
  let nonRebirthRemaining = 0;
  for (const id of deck) if (id !== 'rebirth') nonRebirthRemaining++;
  const eligible = [];
  for (let i = 0; i < deck.length; i++) {
    const id = deck[i];
    if (id === 'rebirth' && nonRebirthRemaining > 0) continue;
    if (exclude.has(id)) continue;
    const c = CARDS.find(x => x.id === id);
    if (!c) continue;
    if (!c.stackable && ownedUnstackable.has(id)) continue;
    eligible.push(i);
  }
  if (eligible.length === 0) return null;
  const pickedDeckIdx = eligible[Math.floor(rand() * eligible.length)];
  const id = deck[pickedDeckIdx];
  deck.splice(pickedDeckIdx, 1);
  return CARDS.find(c => c.id === id);
}

// Draw up to `count` distinct cards from the deck. Stops early if the deck
// runs out of eligible cards. Caller passes the live deck array — it will
// be mutated (drawn cards spliced out). `excludeIds` is an optional set of
// ids that should be treated as already-drawn (used by top-up paths to
// avoid duplicating ids already in the offer).
function drawCards(count, deck, ownedUnstackable, rng, excludeIds = null) {
  const drawn = [];
  const seen = excludeIds ? new Set(excludeIds) : new Set();
  for (let i = 0; i < count; i++) {
    const card = rollFromDeck(deck, seen, ownedUnstackable, rng);
    if (!card) break;
    drawn.push(card);
    seen.add(card.id);
  }
  return drawn;
}

const REROLL_BASE_COST = 10;
// Each subsequent reroll costs 4x the previous one (10, 40, 160, 640).
// Geometric growth turns reroll into a luxury — caps "spam reroll until
// I get the perfect offer" exploits seen in 2026-05-09 outlier sessions.
const REROLL_COST_MULT = 4;
// Starting draft size. Players begin with 3 choices; the `draftSize`
// card bumps this up by +1 per pick, capped at DRAFT_SIZE_MAX. Smaller
// initial offer makes early picks more meaningful + leaves headroom for
// progression cards to feel impactful.
const DRAFT_SIZE_MIN = 3;
const DRAFT_SIZE_MAX = 5;

// Sustain cards used by novice draft bias (Track A.3). If a first-
// session offer lacks any of these, _applyNoviceBias swaps one in.
// Order = preference: regen first (highest impact for chip-damage
// waves), then lifesteal, then maxHp (extra copy of the starter-kit
// card still useful for the heal-on-pick).
const NOVICE_SUSTAIN_IDS = ['regen', 'lifesteal', 'maxHp'];

// Draft can be entered from two places. Both modes charge gold per pick
// via getCardCost(); they differ in trigger + per-wave allowance.
//   INTERWAVE — auto-triggered between waves (heals 20% on entry).
//   SHOP      — walk-in shop building (hut). Capped at one purchase per wave.
export const DraftMode = {
  INTERWAVE: 'interwave',
  SHOP: 'shop',
};

export class UI {
  constructor() {
    // Track upgrade/card purchase counts per id (for menu stats / unstackable check).
    this.cardCounts = {};
    // Current draft slot count. Starts at DRAFT_SIZE_MIN, grows via the
    // 'draftSize' card up to DRAFT_SIZE_MAX. Persists between waves but
    // resets in reset() (new run).
    this.draftSize = DRAFT_SIZE_MIN;
    // Cards on current draft offer.
    this.draftOffer = [];
    // Current draft mode — determines whether picks cost gold.
    this.draftMode = DraftMode.INTERWAVE;
    // Reroll counter for this shop visit. Reset on each begin*Draft call.
    this.rerollCount = 0;
    // Run-lifetime deck. Picked cards are removed; unpicked cards persist in
    // the offer across waves; rerolls mill the offer back. No refill once
    // empty — design enforces a finite, depleting card economy.
    this.deck = buildDeck();
    // At most one shop purchase per wave. Reset in onWaveStart().
    this._shopPicksThisWave = 0;
    // Set by pickCard when the player takes the rebirth sentinel. The Game
    // polls this each frame after a pick to drive the cutscene + run reset.
    // Cleared either by consumeRebirthPending() or by reset() at run end.
    this.rebirthPending = false;
    // Novice draft bias (Track A.3). When >0, the next interwave draft
    // guarantees at least one sustain card (lifesteal/regen/maxHp) in the
    // offer. Game.js sets this to 2 at startGame for first-ever sessions
    // so the player sees a healing option in their first two drafts. The
    // counter decrements per applied bias, not per draft, so a draft that
    // already had a sustain card by luck doesn't burn a bias slot.
    this._noviceBiasRemaining = 0;

    // Interaction rects set during render.
    this._cardButtons = [];
    this._rerollButton = null;
    this._skipButton = null;

    // On-screen attack button (rect set during render). Rendered only on
    // touch-capable devices; consulted by Input/Game for hit-testing.
    this.attackBtn = null;
    this._isTouchDevice = (typeof window !== 'undefined') && (
      ('ontouchstart' in window) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
    );
  }

  reset() {
    this.cardCounts = {};
    this.draftSize = DRAFT_SIZE_MIN;
    this.draftOffer = [];
    this.draftMode = DraftMode.INTERWAVE;
    this.rerollCount = 0;
    this.deck = buildDeck();
    this._shopPicksThisWave = 0;
    this.rebirthPending = false;
    this._cardButtons = [];
    this._rerollButton = null;
    this._skipButton = null;
    this._noviceBiasRemaining = 0;
  }

  _ownedUnstackable() {
    const set = new Set();
    for (const id in this.cardCounts) {
      const card = CARDS.find(c => c.id === id);
      if (card && !card.stackable && this.cardCounts[id] > 0) set.add(id);
    }
    // Once draftSize hits MAX further copies do nothing, so drop the
    // card from the offer pool to free a slot for useful picks.
    if (this.draftSize >= DRAFT_SIZE_MAX) set.add('draftSize');
    return set;
  }

  // Top up the persistent offer to draftSize. Existing cards stay put; the
  // empty slots (vacated by past picks) get filled from the deck without
  // duplicating any id already on offer. Stops short if the deck has no
  // more eligible cards.
  _topUpOffer(rng = null) {
    const needed = this.draftSize - this.draftOffer.length;
    if (needed <= 0) return;
    const exclude = new Set(this.draftOffer.map(c => c.id));
    const fresh = drawCards(needed, this.deck, this._ownedUnstackable(), rng, exclude);
    this.draftOffer.push(...fresh);
  }

  // Begin an interwave draft (between waves). Heals the player for 20% of max
  // HP as a "breath between waves" — applied before any card draw so it
  // doesn't interact with regen/lifesteal gating. The offer is persistent
  // across waves and the interwave/shop split: cards picked earlier stay
  // gone, but every visit tops up the offer to draftSize so the player
  // sees a full row whenever the deck still has stock.
  beginInterwaveDraft(rng = null, player = null) {
    this.draftMode = DraftMode.INTERWAVE;
    this.rerollCount = 0;
    if (player && player.alive) {
      const heal = Math.floor(player.maxHp * 0.2);
      player.hp = Math.min(player.maxHp, player.hp + heal);
    }
    this._topUpOffer(rng);
    this._applyNoviceBias();
  }

  // Novice draft bias (Track A.3). If `_noviceBiasRemaining > 0` and the
  // current offer lacks any sustain card, swap the last offered slot for
  // a sustain card from the deck. Decrements the counter only when a
  // swap actually fires — a draft that organically had sustain doesn't
  // burn a slot. Deck inventory stays exact via an in-place swap (the
  // displaced card's id replaces the sustain's deck slot, preserving
  // deck size without a separate splice/push roundtrip).
  _applyNoviceBias() {
    if (this._noviceBiasRemaining <= 0) return;
    if (this.draftOffer.length === 0) return;
    if (this.draftOffer.some(c => NOVICE_SUSTAIN_IDS.includes(c.id))) return;

    const owned = this._ownedUnstackable();
    for (const id of NOVICE_SUSTAIN_IDS) {
      const card = CARDS.find(c => c.id === id);
      if (!card) continue;
      if (!card.stackable && owned.has(id)) continue;
      const deckIdx = this.deck.indexOf(id);
      if (deckIdx < 0) continue;
      const dropped = this.draftOffer[this.draftOffer.length - 1];
      this.deck[deckIdx] = dropped.id;
      this.draftOffer[this.draftOffer.length - 1] = card;
      this._noviceBiasRemaining--;
      return;
    }
  }

  // Begin a shop draft (in-world shop building). Shares the same persistent
  // offer as the interwave draft — what the player saw between waves is what
  // they see in the shop, minus anything already picked. Empty slots top up
  // from the deck on entry. Once the per-wave shop allowance is spent the
  // renderer shows a "sold out" state via shopPickLimitReached() and we
  // skip the top-up (no point drawing a card the player can't buy this wave).
  beginShopDraft(rng = null) {
    this.draftMode = DraftMode.SHOP;
    this.rerollCount = 0;
    if (!this.shopPickLimitReached()) {
      this._topUpOffer(rng);
    }
  }

  // True once the player has used up their one shop pick for the current
  // wave. Used by the shop renderer to show a "sold out" message and by
  // game.js to route re-entry attempts sensibly.
  shopPickLimitReached() {
    return this._shopPicksThisWave >= 1;
  }

  // Called by game when a new wave starts — refreshes the per-wave shop pick
  // allowance. The offer itself is persistent and is NOT cleared here.
  onWaveStart() {
    this._shopPicksThisWave = 0;
  }

  // Shop cost scales geometrically with stacks already owned: each
  // subsequent copy costs 3x the previous (1x, 3x, 9x, 27x ...). Hard
  // gold sink so post-boss caches don't pile up unspent — players have
  // to either diversify or save for big purchases.
  getCardCost(card) {
    if (!card) return 0;
    const base = RARITY_COST[card.rarity] || 0;
    const owned = this.cardCounts[card.id] || 0;
    return Math.floor(base * Math.pow(3, owned));
  }

  getRerollCost() {
    return Math.floor(REROLL_BASE_COST * Math.pow(REROLL_COST_MULT, this.rerollCount));
  }

  tryReroll(player, rng = null) {
    if (this.draftMode === DraftMode.SHOP && this._shopPicksThisWave >= 1) return false;
    const cost = this.getRerollCost();
    if (!player || player.gold < cost) return false;
    player.gold -= cost;
    this.rerollCount++;
    // Mill the unpicked offer back into the deck, then draw fresh.
    for (const card of this.draftOffer) this.deck.push(card.id);
    this.draftOffer = drawCards(this.draftSize, this.deck, this._ownedUnstackable(), rng);
    return true;
  }

  // Pick a card from the current offer. Always deducts gold. In world-shop
  // (PAID) mode, also enforces the per-wave pick limit.
  // Returns true if a card was applied.
  pickCard(index, player) {
    if (!player) return false;
    if (index < 0 || index >= this.draftOffer.length) return false;
    const card = this.draftOffer[index];
    if (!card) return false;

    // Per-wave limit: world shop only (wave-end draft is one-and-done by flow).
    if (this.draftMode === DraftMode.SHOP && this._shopPicksThisWave >= 1) return false;

    // All cards cost gold.
    const cost = this.getCardCost(card);
    if (player.gold < cost) return false;
    player.gold -= cost;

    const owned = this.cardCounts[card.id] || 0;
    card.apply(player, stackScale(owned));
    this.cardCounts[card.id] = owned + 1;

    // 'draftSize' bumps the slot count for future drafts. Capped so a
    // long run can't sprawl across the screen, and exclusion at MAX
    // keeps the offer pool clean (the card stops appearing).
    if (card.id === 'draftSize' && this.draftSize < DRAFT_SIZE_MAX) {
      this.draftSize = Math.min(DRAFT_SIZE_MAX, this.draftSize + 1);
    }

    if (this.draftMode === DraftMode.SHOP) {
      this._shopPicksThisWave++;
    }
    if (card.id === 'rebirth') {
      this.rebirthPending = true;
    }
    // Remove only the picked card from the offer; the rest persist across
    // waves and shop visits until they're picked or milled by a reroll.
    this.draftOffer.splice(index, 1);
    return true;
  }

  // One-shot consumer for the rebirth flag: returns true exactly once after
  // the player picks the rebirth card, then resets the flag. The Game uses
  // this to enter the cutscene state without missing the trigger if
  // pickCard is called multiple times in quick succession.
  consumeRebirthPending() {
    const was = this.rebirthPending;
    this.rebirthPending = false;
    return was;
  }

  // Hit-test the card offers; returns index or -1.
  handleDraftClick(mouseX, mouseY) {
    for (let i = 0; i < this._cardButtons.length; i++) {
      const btn = this._cardButtons[i];
      if (pointInRect(mouseX, mouseY, btn.x, btn.y, btn.w, btn.h)) {
        return i;
      }
    }
    return -1;
  }

  isRerollClicked(mouseX, mouseY) {
    if (!this._rerollButton) return false;
    const b = this._rerollButton;
    return pointInRect(mouseX, mouseY, b.x, b.y, b.w, b.h);
  }

  isSkipClicked(mouseX, mouseY) {
    if (!this._skipButton) return false;
    const b = this._skipButton;
    return pointInRect(mouseX, mouseY, b.x, b.y, b.w, b.h);
  }

  // --- Render methods ---

  // Top-right fullscreen toggle. Stores hit-rect in `this.fullscreenBtn` so
  // a capture-phase mouse listener in main.js can intercept clicks before
  // the menu/pause/gameover screens treat them as "advance" gestures.
  renderFullscreenButton(r) {
    // iPhone (Safari and the iOS app's WKWebView) has no element Fullscreen
    // API — the button would be dead there; the app is fullscreen anyway.
    if (typeof document !== 'undefined'
        && !(document.fullscreenEnabled || document.webkitFullscreenEnabled)) {
      this.fullscreenBtn = null;
      return;
    }
    const size = 36;
    const margin = 16;
    const x = r.width - margin - size;
    const y = margin;
    this.fullscreenBtn = { x, y, w: size, h: size };

    r.setAlpha(0.45);
    r.rect(x, y, size, size, '#000');
    r.resetAlpha();
    r.strokeRect(x, y, size, size, '#888', 1);
    r.text('⛶', x + size / 2, y + size / 2 + 1, '#ffd700', 22, 'center', 'middle');
    if (typeof document !== 'undefined' && document.fullscreenElement) {
      // Diagonal slash signals "click again to exit fullscreen".
      r.line(x + 6, y + 6, x + size - 6, y + size - 6, '#cc6666', 2);
    }
  }

  renderMenu(r, game) {
    const cx = r.width / 2;
    const cy = r.height / 2;
    const now = performance.now() / 1000;

    r.setAlpha(0.75);
    r.rect(0, 0, r.width, r.height, '#1a1a2e');
    r.resetAlpha();

    const swordY = cy - 100;
    r.setAlpha(0.2);
    r.line(cx - 80, swordY - 30, cx + 80, swordY + 30, '#ffd700', 3);
    r.line(cx + 80, swordY - 30, cx - 80, swordY + 30, '#ffd700', 3);
    r.resetAlpha();

    // Title and subtitle are pixel lettering on the sprite grid. The glow
    // pulses in three steps rather than smoothly, like the rest of the
    // pixel art.
    const pulseStep = Math.round((0.5 + 0.5 * Math.sin(now * 2)) * 2);
    r.pixelText(t('menu.title'), cx, cy - 100, MENU_TITLE_STYLE, 0.12 + 0.09 * pulseStep);

    r.setAlpha(0.5);
    r.line(cx - 100, cy - 55, cx + 100, cy - 55, '#ffd700', 1);
    r.resetAlpha();

    r.pixelText(t('menu.subtitle'), cx, cy - 30, MENU_SUBTITLE_STYLE);

    // Best record
    const bestScore = getBestScore();
    const bestWave = getBestWave();
    if (bestScore > 0 || bestWave > 0) {
      r.textOutlined(
        t('menu.record', { score: bestScore, wave: bestWave }),
        cx, cy + 10, '#ffc85a', '#000', 16, 'center', 'middle'
      );
    }

    // Unlocked relics column. Hidden until the player drops their first
    // relic so a fresh menu stays uncluttered. Each slot is clickable
    // and toggles active state — main.js installs the handler that
    // reads `this.relicSlots`. Drawn before the hints, which keep clear
    // of it.
    this._renderMenuRelics(r);

    // Phones (iOS app, TWA, mobile browsers) get touch hints — keyboard
    // ones would describe controls the player doesn't have.
    const hints = this._isTouchDevice
      ? [
        { str: t('menu.touchControls1'), color: '#aaa', size: 16 },
        { str: t('menu.touchControls2'), color: '#aaa', size: 12 },
      ]
      : [
        { str: t('menu.controls1'), color: '#aaa', size: 16 },
        { str: t('menu.controls2'), color: '#aaa', size: 12 },
        { str: t('menu.controls3'), color: '#888', size: 13 },
      ];
    // Centred, so the relic column's width is kept clear on both sides.
    const hintMargin = Math.max(16, this._relicColumnRight + 8);
    const hintMaxW = r.width - hintMargin * 2;
    // Room between the record line and the start prompt's usual spot.
    const hintTop = cy + 45 - hints[0].size / 2;
    const rows = this._layoutMenuHints(r, hints, hintMaxW, cy + 152 - hintTop);
    for (const row of rows) {
      r.textOutlined(row.str, cx, hintTop + row.y, row.color, '#000', row.size, 'center', 'middle');
    }
    const last = rows[rows.length - 1];
    const hintBottom = hintTop + last.y + last.size / 2;

    // Coins and the start prompt step down when the hints ran long, the
    // prompt never past the bottom edge.
    const coinsY = Math.max(cy + 130, hintBottom + 14);
    const promptY = Math.min(r.height - 30, Math.max(cy + 170, coinsY + 40));

    r.setAlpha(0.4);
    for (let i = 0; i < 5; i++) {
      const coinX = cx - 160 + i * 80;
      const coinY = coinsY + Math.sin(now * 1.5 + i * 1.3) * 8;
      r.circle(coinX, coinY, 4, '#ffd700');
      r.circle(coinX - 1, coinY - 1, 2, '#ffee66');
    }
    r.resetAlpha();

    const promptAlpha = 0.5 + 0.5 * Math.sin(now * 3);
    r.setAlpha(promptAlpha);
    r.textOutlined(t(this._isTouchDevice ? 'menu.touchStart' : 'menu.start'), cx, promptY, '#fff', '#000', 20, 'center', 'middle');
    r.resetAlpha();

    // Prefer semver when present; fall back to commit short (older bundles
    // built before scripts/bump-version.sh existed don't have version).
    const semver = game.build && game.build.version;
    const buildShort = (game.build && game.build.short) || '';
    const versionLabel = semver ? `v ${semver}` : (buildShort ? `v ${buildShort}` : '');
    r.setAlpha(0.55);
    r.text(versionLabel, r.width - 40, r.height - 20, '#e0e0e0', 12, 'right');
    r.resetAlpha();

    this.renderUpdateBanner(r, game);
    this.renderMenuLinks(r);
    this.renderFullscreenButton(r);
    this.renderLangButton(r);
  }

  // Top-right EN/RU switch, left of the fullscreen button (in the corner
  // itself where that one is hidden, e.g. iPhone). Shows the language it
  // switches to. Rides on `this.menuLinks`, so main.js routes its clicks.
  renderLangButton(r) {
    const size = 36;
    const margin = 16;
    const fs = this.fullscreenBtn;
    const x = (fs ? fs.x - 8 : r.width - margin) - size;
    const y = margin;
    const label = getLang() === 'ru' ? 'EN' : 'RU';
    r.setAlpha(0.45);
    r.rect(x, y, size, size, '#000');
    r.resetAlpha();
    r.strokeRect(x, y, size, size, '#888', 1);
    r.text(label, x + size / 2, y + size / 2 + 1, '#ffd700', 14, 'center', 'middle');
    this.menuLinks.push({ x, y, w: size, h: size, action: 'lang' });
  }

  // Top-left text buttons: privacy policy (Play requires an in-app
  // link) and the telemetry opt-out toggle. Top row, because the bottom
  // is taken by the start prompt on 360 px landscape phones and the
  // relic column starts at y >= 40. Hit-rects go to `this.menuLinks`
  // for the capture-phase listener in main.js.
  renderMenuLinks(r) {
    this.menuLinks = [];
    const fontSize = 12;
    const padX = 8;
    const h = fontSize + 10;
    const y = 10;
    let x = 10;
    const items = [
      { action: 'privacy', label: t('menu.privacy') },
      { action: 'telemetry', label: t(isTelemetryOptedOut() ? 'menu.telemetryOff' : 'menu.telemetryOn') },
    ];
    for (const it of items) {
      const w = r.measureText(it.label, fontSize) + padX * 2;
      r.setAlpha(0.7);
      r.rect(x, y, w, h, '#000');
      r.resetAlpha();
      r.strokeRect(x, y, w, h, '#666', 1);
      r.text(it.label, x + w / 2, y + h / 2 + 1, '#ccc', fontSize, 'center', 'middle');
      this.menuLinks.push({ x, y, w, h, action: it.action });
      x += w + 8;
    }
  }

  // Wraps the menu hints into rows no wider than maxW: whole parts of a
  // hint share a row while they fit; a part wider than a row gets rows of
  // its own, broken before a parenthesis, then between words.
  // If the rows are taller than maxH, every size steps down by 1 px, to
  // MENU_HINT_MIN_SIZE at the least (then the rows may run taller).
  // Returns [{ str, color, size, y }], y = row centre below the block top.
  _layoutMenuHints(r, hints, maxW, maxH) {
    for (let shrink = 0; ; shrink++) {
      const rows = [];
      let y = 0;
      let minned = true;
      for (let i = 0; i < hints.length; i++) {
        const h = hints[i];
        const size = Math.max(MENU_HINT_MIN_SIZE, h.size - shrink);
        if (size > MENU_HINT_MIN_SIZE) minned = false;
        const lines = this._wrapMenuHint(r, h.str, size, maxW);
        for (let j = 0; j < lines.length; j++) {
          if (rows.length) y += i > 0 && j === 0 ? MENU_HINT_GAP : MENU_HINT_ROW_GAP;
          rows.push({ str: lines[j], color: h.color, size, y: y + size / 2 });
          y += size;
        }
      }
      if (y <= maxH || minned) return rows;
    }
  }

  _wrapMenuHint(r, str, size, maxW) {
    const lines = [];
    let joinable = false;  // the last line holds whole parts only
    for (const part of str.split('•').map(p => p.trim()).filter(Boolean)) {
      if (r.measureText(part, size) <= maxW) {
        const joined = joinable ? lines[lines.length - 1] + MENU_HINT_SEP + part : null;
        if (joined && r.measureText(joined, size) <= maxW) lines[lines.length - 1] = joined;
        else lines.push(part);
        joinable = true;
        continue;
      }
      for (const chunk of part.split(/ (?=\()/)) {
        let cur = '';
        for (const word of chunk.split(/\s+/)) {
          const test = cur ? cur + ' ' + word : word;
          if (cur && r.measureText(test, size) > maxW) { lines.push(cur); cur = word; }
          else cur = test;
        }
        if (cur) lines.push(cur);
      }
      joinable = false;
    }
    return lines;
  }

  // Relic toggle column on the left edge of the main menu. Inactive
  // slots tuck right up against the edge; active ones slide out toward
  // the center like a card pulled halfway from a deck. Position is
  // animated per slot via `this._relicSlotX` (id → current pixel x)
  // so toggles read as a slide rather than a snap.
  //
  // Stores per-slot hit-rects in `this.relicSlots` so main.js can route
  // clicks to setRelicActive. Hidden when nothing is unlocked yet.
  _renderMenuRelics(r) {
    this.relicSlots = [];
    // Right edge the column settles at (slots mid-slide don't count),
    // for the menu hints to keep clear of; 0 with no column.
    this._relicColumnRight = 0;
    const unlocked = getUnlockedRelics();
    if (unlocked.size === 0) return;
    const active = getActiveRelics();
    const items = RELICS.filter(rel => unlocked.has(rel.id));
    if (items.length === 0) return;

    // Slot size shrinks on short viewports so the column stops past
    // the bottom edge on landscape phones (740×360 fit 7 relics @ 38 px
    // ended at y=374 > 360). availH leaves a 40 px top + bottom margin.
    const n = items.length;
    const maxSize = 38;
    const minSize = 22;
    const availH = r.height - 80;
    const sizeFit = Math.floor(availH / (n + (n - 1) * 0.21));
    const size = Math.max(minSize, Math.min(maxSize, sizeFit));
    const gap = Math.max(4, Math.round(size * 0.21));
    const tuckedX = -size * 0.55;  // mostly hidden behind the edge
    const extendedX = 8;            // pulled out, fully visible
    const totalH = n * size + (n - 1) * gap;
    const startY = Math.max(40, Math.floor((r.height - totalH) / 2));
    const anyOn = items.some(rel => active.has(rel.id));
    this._relicColumnRight = (anyOn ? extendedX : tuckedX) + size;

    if (!this._relicSlotX) this._relicSlotX = new Map();
    // Frame-rate-aware lerp speed: at 60 fps the slot covers the gap
    // in roughly 0.15 s. Tied to wall-clock dt so a 30 fps display
    // still animates at the same speed.
    const now = performance.now();
    const dt = this._relicAnimLast ? Math.min(0.1, (now - this._relicAnimLast) / 1000) : 1 / 60;
    this._relicAnimLast = now;
    const easeAmount = 1 - Math.exp(-dt * 12);

    for (let i = 0; i < items.length; i++) {
      const rel = items[i];
      const y = startY + i * (size + gap);
      const isOn = active.has(rel.id);
      const targetX = isOn ? extendedX : tuckedX;
      const prev = this._relicSlotX.get(rel.id) ?? targetX;
      const x = prev + (targetX - prev) * easeAmount;
      this._relicSlotX.set(rel.id, x);

      r.setAlpha(isOn ? 1 : 0.55);
      r.rect(x, y, size, size, isOn ? '#2a2310' : '#1a1a24');
      r.strokeRect(x, y, size, size, isOn ? '#e2b44a' : '#555', 2);
      r.textThin(rel.icon, x + size / 2, y + size / 2, '#fff', 22, 'center', 'middle');
      r.resetAlpha();
      this.relicSlots.push({ x, y, w: size, h: size, id: rel.id });
    }
  }

  // Bottom-center banner that appears in the main menu when the
  // version-check module detected a newer build on the server. Click /
  // tap reloads — the hit-rect is stored in `this.updateBtn` for the
  // capture-phase listener in main.js to intercept.
  renderUpdateBanner(r, game) {
    if (!game.updateAvailable) {
      this.updateBtn = null;
      return;
    }
    const cx = r.width / 2;
    const stamp = game.updateAvailable.version || game.updateAvailable.short || '';
    const label = t('menu.updateAvailable', { short: stamp });
    const fontSize = 14;
    const padX = 14;
    const padY = 8;
    const textW = r.measureText(label, fontSize);
    const w = textW + padX * 2;
    const h = fontSize + padY * 2;
    const x = Math.round(cx - w / 2);
    const y = r.height - 50 - h;
    this.updateBtn = { x, y, w, h };

    const pulse = 0.6 + 0.4 * Math.sin(performance.now() / 400);
    r.setAlpha(pulse);
    r.rect(x, y, w, h, '#2a3a5a');
    r.resetAlpha();
    r.strokeRect(x, y, w, h, '#5a8acc', 2);
    r.textOutlined(label, cx, y + h / 2, '#ffffff', '#000', fontSize, 'center', 'middle');
  }

  renderHUD(r, game) {
    const player = game.player;
    r.rect(0, 0, r.width, CONST.HUD_HEIGHT, 'rgba(0,0,0,0.5)');

    const narrow = r.width < 720;
    const isBossWave = game.wave > 0 && game.wave % 5 === 0;
    const waveColor = isBossWave ? '#ff4444' : '#fff';
    const waveKey = isBossWave ? 'hud.waveBoss' : 'hud.wave';

    // Right-anchored cluster (built right-to-left). rightX tracks the
    // leftmost x consumed so the left cluster knows where to stop.
    let rightX = r.width - 10;
    if (game.audio && game.audio.muted) {
      r.text('🔇', rightX, 10, '#ffaa00', 18, 'right');
      rightX -= 28;
    }
    // Auto-attack badge. Only shown when on — it's a mode the player opted
    // into and needs a persistent reminder of, but off is the default and
    // needs no chrome.
    if (game.autoAttack) {
      r.text('🔒', rightX, 10, '#ffd700', 18, 'right');
      rightX -= 28;
    }
    const aliveCaravans = game.caravans.filter(c => c.alive).length;
    const aliveGuards = game.guards.filter(g => g.alive).length;
    if (narrow) {
      const guardsStr = `⚔ ${aliveGuards}`;
      r.text(guardsStr, rightX, 10, '#ddd', 14, 'right');
      rightX -= r.measureText(guardsStr, 14) + 12;
      const caravansStr = `📦 ${aliveCaravans}`;
      r.text(caravansStr, rightX, 10, '#ddd', 14, 'right');
      rightX -= r.measureText(caravansStr, 14) + 12;
    } else {
      const cgStr = t('hud.caravansGuards', { caravans: aliveCaravans, guards: aliveGuards });
      r.text(cgStr, rightX, 10, '#ddd', 14, 'right');
      rightX -= r.measureText(cgStr, 14) + 12;
    }

    // Left cluster: wave, score, HP bar, gold, dash. Each item is
    // "soft" past wave — if it doesn't fit before the right cluster
    // (`rightX`) we drop it entirely instead of letting it overflow.
    // Wave is mandatory (without it the player has no progress signal);
    // everything after it falls off in priority order on narrow screens.
    const PAD = 8; // breathing space before rightX
    let leftX = 10;
    const fits = (need) => leftX + need + PAD <= rightX;
    const waveStr = t(waveKey, { wave: game.wave }) + (game.rebirths > 0 ? ' ' + '★'.repeat(game.rebirths) : '');
    r.text(waveStr, leftX, 10, waveColor, 16);
    leftX += r.measureText(waveStr, 16) + 16;

    const scoreStr = t('hud.score', { score: game.score });
    const scoreW = r.measureText(scoreStr, 16);
    if (fits(scoreW)) {
      r.text(scoreStr, leftX, 10, CONST.COLOR_GOLD, 16);
      leftX += scoreW + 16;
    }

    if (player) {
      const hpBarW = narrow ? 60 : 100;
      if (fits(hpBarW)) {
        r.healthBar(leftX, 12, hpBarW, 14, player.hp / player.maxHp, CONST.COLOR_HP_BAR, CONST.COLOR_HP_BG);
        leftX += hpBarW + 8;
        // HP can be fractional (fortify heals); ceil so a living player never reads 0.
        const hpStr = `${Math.ceil(player.hp)}/${Math.round(player.maxHp)}`;
        const hpW = r.measureText(hpStr, 14);
        if (fits(hpW)) {
          r.text(hpStr, leftX, 10, '#fff', 14);
          leftX += hpW + 12;
        }
      }

      const coinSize = 14;
      const goldStr = `${player.gold}`;
      const goldW = r.measureText(goldStr, 16);
      if (fits(coinSize + 4 + goldW)) {
        r.coinIcon(leftX + coinSize / 2, 17, coinSize);
        leftX += coinSize + 4;
        r.text(goldStr, leftX, 10, CONST.COLOR_GOLD, 16);
        leftX += goldW + 12;
      }

      // Dash cooldown bar + bolt icon. Hidden when it would collide with
      // the right cluster on cramped HUDs.
      const dashY = 14;
      const dashW = 30;
      const dashH = 10;
      const dashIconPad = 18;
      if (fits(dashW + dashIconPad)) {
        const dashReady = player.dashCooldownTimer <= 0;
        const frac = dashReady ? 1 : 1 - (player.dashCooldownTimer / player.dashCooldownMax);
        r.rect(leftX, dashY, dashW, dashH, '#222');
        r.rect(leftX, dashY, Math.round(dashW * frac), dashH, dashReady ? '#ffd700' : '#886622');
        r.text('⚡', leftX + dashW + 4, 10, dashReady ? '#ffd700' : '#666', 14);
        leftX += dashW + dashIconPad + 12;
      }

      // Super strike meter + [Q] hint. Desktop only: on touch the ring on
      // the super button is the meter.
      const superW = 46;
      const superPad = 34;
      if (!this._isTouchDevice && fits(superW + superPad)) {
        const ready = player.superReady;
        const frac = Math.min(1, player.superCharge / CONST.SUPER_CHARGE_MAX);
        r.rect(leftX, dashY, superW, dashH, '#222');
        r.rect(leftX, dashY, Math.round(superW * frac), dashH, ready ? '#ffb347' : '#8a5a2a');
        r.text(ready ? '💥Q' : '💥', leftX + superW + 4, 10, ready ? '#ffb347' : '#666', 14);
        leftX += superW + superPad;
      }
    }

    this.renderActiveEffects(r, game);
    this.renderAppliedCards(r, game);
  }

  // Row of miniature icons in the bottom-left: active relics first, then
  // owned cards with stack counts. Relics use a gold border to set them
  // apart from rarity-tinted card borders. CARDS order is used for
  // cards (not pick order) so the row is stable across picks; relics
  // follow RELICS order for the same reason.
  renderAppliedCards(r, game) {
    const owned = CARDS.filter(c => (this.cardCounts[c.id] || 0) > 0);
    const activeRelics = (game && game.activeRelics) ? game.activeRelics : new Set();
    const relics = RELICS.filter(rel => activeRelics.has(rel.id));
    const slotCount = relics.length + owned.length;
    if (slotCount === 0) return;

    const size = 28;
    const gap = 4;
    const relicGap = relics.length && owned.length ? 8 : 0;
    const totalW = slotCount * size + (slotCount - 1) * gap + relicGap;
    const padX = 4;
    const padY = 4;
    const startX = 10;
    const y = r.height - 10 - size;

    r.setAlpha(0.45);
    r.rect(startX - padX, y - padY, totalW + padX * 2, size + padY * 2, '#000');
    r.resetAlpha();

    let x = startX;
    for (const rel of relics) {
      r.rect(x, y, size, size, '#2a2310');
      r.strokeRect(x, y, size, size, '#e2b44a', 2);
      r.textThin(rel.icon, x + size / 2, y + size / 2, '#fff', 18, 'center', 'middle');
      x += size + gap;
    }
    if (relics.length && owned.length) x += relicGap - gap;
    for (const card of owned) {
      const count = this.cardCounts[card.id] || 0;
      r.rect(x, y, size, size, '#222237');
      r.strokeRect(x, y, size, size, RARITY_COLOR[card.rarity], 2);
      r.textThin(card.icon, x + size / 2, y + size / 2, '#fff', 18, 'center', 'middle');
      if (count > 1) {
        r.textOutlined(`x${count}`, x + size - 2, y + size - 1, '#ffd700', '#000', 10, 'right', 'bottom');
      }
      x += size + gap;
    }
  }

  // Active temporary effects (above the cards row). Each effect is icon +
  // remaining seconds. Only renders the effects that are currently active —
  // empty row when player has no DoT and isn't in i-frames. New effects
  // (poison, temp buffs) plug in by extending the `effects` array below.
  renderActiveEffects(r, game) {
    const player = game.player;
    if (!player) return;
    const effects = [];
    if (player.iframeTimer > 0) {
      effects.push({ icon: '🛡', timer: player.iframeTimer, color: '#9adfff' });
    }
    if (player.burnTimer > 0) {
      effects.push({ icon: '🔥', timer: player.burnTimer, color: '#ff8844' });
    }
    // Sticky arrow. Worth its own row rather than folding into the player
    // sprite: "dash is on cooldown" and "dash is locked out" look identical
    // otherwise, and the player needs to know which one they're in.
    if (player.stickyTimer > 0) {
      effects.push({ icon: '🕸', timer: player.stickyTimer, color: '#9acd32' });
    }
    // Timed buffs looted from guards (rage / frenzy / nerve). Icon + color
    // come from the shared BUFF_DISPLAY map so HUD matches the drop token.
    if (player.buffs && player.buffs.size > 0) {
      for (const [type, remaining] of player.buffs) {
        const d = BUFF_DISPLAY[type];
        if (d) effects.push({ icon: d.icon, timer: remaining, color: d.color });
      }
    }
    if (effects.length === 0) return;

    const size = 28;
    const gap = 8;
    // Sit above the cards row (cards bottom is r.height - 10 - size).
    const y = r.height - 10 - size - 6 - size;
    let x = 10;

    for (const eff of effects) {
      const txt = `${eff.timer.toFixed(1)}s`;
      const txtW = r.measureText(txt, 12);
      const w = size + 4 + txtW;

      r.setAlpha(0.45);
      r.rect(x - 4, y - 4, w + 8, size + 8, '#000');
      r.resetAlpha();

      r.textOutlined(eff.icon, x + size / 2, y + size / 2, eff.color, '#000', 20, 'center', 'middle');
      r.textOutlined(txt, x + size + 4, y + size / 2 + 1, '#fff', '#000', 12, 'left', 'middle');

      x += w + gap;
    }
  }

  // Compute the on-screen attack button rect for touch devices, or null
  // when none should be drawn. Centralized so update + render agree.
  computeAttackButton(r) {
    if (!this._isTouchDevice) return null;
    const size = Math.max(96, Math.min(140, Math.floor(Math.min(r.width, r.height) * 0.18)));
    const margin = 24;
    const x = r.width - margin - size;
    const y = r.height - margin - size;
    return { x, y, w: size, h: size };
  }

  // Dash button sits above the attack button, smaller, same right edge.
  // Returns null when the attack button is also null.
  computeDashButton(r) {
    const atk = this.computeAttackButton(r);
    if (!atk) return null;
    const size = Math.floor(atk.w * 0.7);
    const gap = 16;
    const x = atk.x + atk.w - size; // right-align with attack button
    const y = atk.y - gap - size;
    return { x, y, w: size, h: size };
  }

  // Super button: same size as dash, stacked above it on the same right
  // edge, so the right thumb walks straight up attack → dash → super.
  computeSuperButton(r) {
    const dash = this.computeDashButton(r);
    if (!dash) return null;
    const gap = 16;
    return { x: dash.x, y: dash.y - gap - dash.h, w: dash.w, h: dash.h };
  }

  // Auto-attack lock target: sits left of the attack button, centred on its
  // horizontal line, so "drag the attack thumb left" reaches it without
  // crossing dash or super. Only shown (and only registered with Input)
  // while an attack touch is held, otherwise it would clutter the HUD and
  // steal taps.
  computeAutoLockButton(r) {
    const atk = this.computeAttackButton(r);
    const dash = this.computeDashButton(r);
    if (!atk) return null;
    const size = Math.floor(dash.w * 0.85);
    const gap = 14;
    const x = atk.x - gap - size;
    const y = atk.y + atk.h / 2 - size / 2;
    return { x, y, w: size, h: size };
  }

  // Semi-transparent attack + dash buttons anchored bottom-right for the
  // right thumb. Hold attack to auto-aim, tap dash to use dash cooldown.
  // Also draws the virtual joystick on the left side when the player is
  // dragging a move-touch.
  renderTouchControls(r, game) {
    const atk = this.computeAttackButton(r);
    this.attackBtn = atk;
    if (atk) {
      this._drawTouchButton(r, atk, '⚔', '#ffd700', 1);
    }

    const dash = this.computeDashButton(r);
    this.dashBtn = dash;
    if (dash) {
      const player = game && game.player;
      const ready = player ? player.dashCooldownTimer <= 0 : true;
      const frac = (player && !ready)
        ? Math.max(0, Math.min(1, 1 - player.dashCooldownTimer / player.dashCooldownMax))
        : 1;
      const color = ready ? '#ffd700' : '#886622';
      this._drawTouchButton(r, dash, '⚡', color, frac);
    }

    // Super: the fill ring is the meter; bright once it's full.
    const sup = this.computeSuperButton(r);
    this.superBtn = sup;
    if (sup) {
      const player = game && game.player;
      const frac = player ? Math.min(1, player.superCharge / CONST.SUPER_CHARGE_MAX) : 0;
      this._drawTouchButton(r, sup, '💥', frac >= 1 ? '#ffb347' : '#8a5a2a', frac);
    }

    // Auto-attack lock. The target only exists while the attack thumb is
    // down — drag left to it and release to toggle. When auto-attack is
    // already on, a small persistent badge on the attack button says so.
    const input = game && game.input;
    const drag = input && input.getAttackDragState ? input.getAttackDragState() : null;
    const lock = drag ? this.computeAutoLockButton(r) : null;
    this.autoLockBtn = lock;
    if (input && input.setAutoLockRect) input.setAutoLockRect(lock);
    if (lock && atk) {
      // Guide line from the attack button to the target, so the gesture
      // is discoverable the first time the player holds attack.
      r.setAlpha(drag.overLock ? 0.75 : 0.35);
      r.line(
        atk.x + atk.w / 2, atk.y + atk.h / 2,
        lock.x + lock.w / 2, lock.y + lock.h / 2,
        drag.overLock ? '#ffd700' : '#fff', 2
      );
      r.resetAlpha();
      const on = !!(game && game.autoAttack);
      this._drawTouchButton(
        r, lock,
        on ? '🔓' : '🔒',
        drag.overLock ? '#ffd700' : '#cccccc',
        1
      );
    } else if (atk && game && game.autoAttack) {
      r.setAlpha(0.8);
      r.textOutlined('🔒', atk.x + atk.w - 6, atk.y + 6, '#ffd700', '#000', 18, 'right', 'top');
      r.resetAlpha();
    }

    if (this._isTouchDevice && game && game.input && game.input.getJoystickState) {
      const js = game.input.getJoystickState();
      if (js) this._drawJoystick(r, js);
    }
  }

  _drawJoystick(r, js) {
    // Base ring at the touch anchor; thumb at the clamped current pos.
    r.setAlpha(0.22);
    r.circle(js.ox, js.oy, js.maxRange, '#000');
    r.resetAlpha();
    r.setAlpha(0.55);
    r.strokeCircle(js.ox, js.oy, js.maxRange, '#fff', 2);
    r.resetAlpha();
    r.setAlpha(0.7);
    r.circle(js.tx, js.ty, Math.max(14, js.maxRange * 0.35), js.active ? '#ffd700' : '#888');
    r.resetAlpha();
  }

  _drawTouchButton(r, btn, icon, color, fillFrac) {
    const cx = btn.x + btn.w / 2;
    const cy = btn.y + btn.h / 2;
    const radius = btn.w / 2;
    r.setAlpha(0.28);
    r.circle(cx, cy, radius, '#000');
    r.resetAlpha();
    if (fillFrac < 1) {
      // Cooldown ring: shrink the inner alpha overlay to show progress.
      r.setAlpha(0.18);
      r.circle(cx, cy, radius * (1 - fillFrac), '#fff');
      r.resetAlpha();
    }
    r.setAlpha(0.55);
    r.strokeCircle(cx, cy, radius, color, 2);
    r.resetAlpha();
    r.setAlpha(fillFrac < 1 ? 0.5 : 0.85);
    const size = Math.floor(radius * 0.9);
    const { dx, dy } = r.glyphCentreOffset(icon, size);
    r.textOutlined(icon, cx + dx, cy + dy, color, '#000', size, 'center', 'middle');
    r.resetAlpha();
  }

  renderShop(r, player, game) {
    const cx = r.width / 2;
    const isShop = this.draftMode === DraftMode.SHOP;

    r.rect(0, 0, r.width, r.height, '#1a1a2e');

    // Responsive layout: scales card grid + buttons to viewport. Below
    // ~900 px width (mobile landscape) the original 150 px cards overflow.
    const sidePad = 12;
    const gap = Math.max(6, Math.min(14, Math.floor(r.width * 0.012)));
    const maxCardW = (r.width - 2 * sidePad - (this.draftSize - 1) * gap) / this.draftSize;
    const cardW = Math.floor(Math.max(64, Math.min(150, maxCardW)));
    const cardH = Math.floor(Math.max(180, Math.min(240, r.height * 0.55)));

    const titleSize = Math.max(22, Math.min(38, Math.floor(r.width / 24)));
    const goldSize = Math.max(14, Math.min(20, Math.floor(r.width / 50)));
    const titleY = Math.max(28, Math.min(60, Math.floor(r.height * 0.08)));
    const goldY = titleY + Math.floor(titleSize * 0.95);
    const cardY = goldY + 22;

    const shopTitle = isShop ? t('shop.titleShop') : t('shop.titleInterwave');
    r.textOutlinedFit(shopTitle, cx, titleY, r.width - 80, '#ffd700', '#000', titleSize, 'center', 'middle');
    r.textOutlined(
      t('shop.gold', { gold: player ? player.gold : 0 }),
      cx, goldY, CONST.COLOR_GOLD, '#000', goldSize, 'center', 'middle'
    );

    this._cardButtons = [];
    const totalW = this.draftSize * cardW + (this.draftSize - 1) * gap;
    const startX = cx - totalW / 2;

    const btnW = Math.floor(Math.max(110, Math.min(180, (r.width - 60) / 2)));
    const btnH = Math.max(32, Math.min(40, Math.floor(r.height * 0.07)));
    const btnGap = 12;
    const btnY = cardY + cardH + Math.max(10, Math.floor(r.height * 0.03));
    const btnFontSize = Math.max(12, Math.min(16, Math.floor(btnH * 0.42)));

    // Shop mode: if the per-wave allowance is spent, show a "sold out"
    // message instead of an empty card area.
    if (isShop && this.shopPickLimitReached()) {
      r.textOutlined(t('shop.soldOut'), cx, cardY + cardH / 2 - 10, '#888', '#000', Math.min(32, titleSize), 'center', 'middle');
      r.text(t('shop.soldOutHint'), cx, cardY + cardH / 2 + 26, '#666', 14, 'center');
      this._rerollButton = null;
      const skX = cx - btnW / 2;
      const skY = btnY;
      this._skipButton = { x: skX, y: skY, w: btnW, h: btnH };
      r.rect(skX, skY, btnW, btnH, '#2a3a5a');
      r.strokeRect(skX, skY, btnW, btnH, '#4a6a8a', 2);
      r.textOutlined(t('shop.exit'), skX + btnW / 2, skY + btnH / 2, '#fff', '#000', btnFontSize, 'center', 'middle');
      if (player && skY + btnH + 60 < r.height - 8) {
        this._renderStats(r, player, cx, skY + btnH + 24, r.width);
      }
      this.renderFullscreenButton(r);
      return;
    }

    for (let i = 0; i < this.draftOffer.length; i++) {
      const card = this.draftOffer[i];
      const x = startX + i * (cardW + gap);
      this._cardButtons.push({ x, y: cardY, w: cardW, h: cardH });

      const cost = this.getCardCost(card);
      const canAfford = player && player.gold >= cost;

      // Background + rarity border
      r.rect(x, cardY, cardW, cardH, canAfford ? '#222237' : '#1a1a24');
      r.strokeRect(x, cardY, cardW, cardH, RARITY_COLOR[card.rarity], 3);

      // Card content positions scale with cardH so narrow phones don't
      // collide icon/title/desc/cost.
      const rarityY = cardY + Math.floor(cardH * 0.05);
      const iconY = cardY + Math.floor(cardH * 0.27);
      const titleCardY = cardY + Math.floor(cardH * 0.55);
      const descY = cardY + Math.floor(cardH * 0.65);
      const costY = cardY + cardH - Math.max(18, Math.floor(cardH * 0.09));
      const ownedY = cardY + cardH - 6;

      const iconSize = Math.max(28, Math.min(44, Math.floor(cardW * 0.32)));
      const titleSizeCard = Math.max(12, Math.min(16, Math.floor(cardW * 0.11)));
      const descSize = Math.max(9, Math.min(11, Math.floor(cardW * 0.08)));
      const costSize = Math.max(13, Math.min(16, Math.floor(cardW * 0.11)));
      const raritySize = Math.max(9, Math.min(11, Math.floor(cardW * 0.075)));

      r.text(card.rarity.toUpperCase(), x + cardW / 2, rarityY, RARITY_COLOR[card.rarity], raritySize, 'center');

      const iconColor = canAfford ? '#fff' : '#555';
      r.textOutlined(card.icon, x + cardW / 2, iconY, iconColor, '#000', iconSize, 'center', 'middle');

      r.textOutlinedFit(
        card.label,
        x + cardW / 2, titleCardY, cardW - 12,
        canAfford ? '#fff' : '#666', '#000', titleSizeCard, 'center', 'middle'
      );

      const descPad = 8;
      // Show the bonus the next pick will actually grant, factoring in
      // diminishing-returns scaling for stackable cards already owned.
      // `ownedNow` is passed as the second arg so discrete-stack cards
      // (Rhythm) can show the actual next trigger instead of scaling math.
      const ownedNow = this.cardCounts[card.id] || 0;
      const descScale = card.stackable ? stackScale(ownedNow) : 1;
      const descStr = card.descAt ? card.descAt(descScale, ownedNow) : card.desc;
      r.textWrapped(descStr, x + cardW / 2, descY, cardW - descPad * 2, canAfford ? '#bbb' : '#555', descSize, 'center');

      const costColor = canAfford ? CONST.COLOR_GOLD : '#664400';
      const costStr = `${cost}`;
      const coinIconSize = costSize;
      const costTextW = r.measureText(costStr, costSize);
      const totalW2 = costTextW + 4 + coinIconSize;
      const startX2 = x + cardW / 2 - totalW2 / 2;
      r.textOutlined(
        costStr,
        startX2 + costTextW / 2, costY,
        costColor, '#000', costSize, 'center', 'middle'
      );
      // Dim the coin when unaffordable to match the price color.
      if (canAfford) {
        r.coinIcon(startX2 + costTextW + 4 + coinIconSize / 2, costY, coinIconSize);
      } else {
        r.setAlpha(0.35);
        r.coinIcon(startX2 + costTextW + 4 + coinIconSize / 2, costY, coinIconSize);
        r.resetAlpha();
      }
      const owned = this.cardCounts[card.id] || 0;
      if (owned > 0) {
        // Anchor to bottom — with default baseline='top' the glyph
        // (10 px tall) would push 4 px past the card frame at the
        // ownedY=cardY+cardH-6 origin chosen here.
        r.text(`x${owned}`, x + cardW / 2, ownedY, '#888', 10, 'center', 'bottom');
      }
    }

    // Reroll + Skip buttons (centered row, scaled with viewport).
    const rerollCost = this.getRerollCost();
    const canReroll = player && player.gold >= rerollCost;
    const rrX = cx - btnW - btnGap / 2;
    const rrY = btnY;
    this._rerollButton = { x: rrX, y: rrY, w: btnW, h: btnH };

    r.rect(rrX, rrY, btnW, btnH, canReroll ? '#3a2a5a' : '#2a2a3a');
    r.strokeRect(rrX, rrY, btnW, btnH, canReroll ? '#6a4a8a' : '#4a4a5a', 2);
    r.textOutlinedFit(
      t('shop.reroll', { cost: rerollCost }),
      rrX + btnW / 2, rrY + btnH / 2, btnW - 12,
      canReroll ? '#fff' : '#666', '#000', btnFontSize, 'center', 'middle'
    );

    const skX = cx + btnGap / 2;
    const skY = rrY;
    this._skipButton = { x: skX, y: skY, w: btnW, h: btnH };
    r.rect(skX, skY, btnW, btnH, '#2a3a5a');
    r.strokeRect(skX, skY, btnW, btnH, '#4a6a8a', 2);
    const skLabel = isShop ? t('shop.exit') : t('shop.skip');
    r.textOutlinedFit(skLabel, skX + btnW / 2, skY + btnH / 2, btnW - 12, '#fff', '#000', btnFontSize, 'center', 'middle');

    // Stats summary — only if there is room below the buttons.
    if (player && rrY + btnH + 60 < r.height - 8) {
      this._renderStats(r, player, cx, rrY + btnH + 22, r.width);
    }

    // Fullscreen toggle in the corner — same button main.js intercepts
    // on menu/paused/gameover, now also available from the shop.
    this.renderFullscreenButton(r);
  }

  _renderStats(r, player, cx, sy, viewportW) {
    // Distribute five columns across available width so narrow phones don't
    // chop off the leftmost / rightmost stats.
    const w = Math.max(320, Math.min(820, (viewportW || 820) - 24));
    const colDx = w / 5;
    const cols = [cx - colDx * 2, cx - colDx, cx, cx + colDx, cx + colDx * 2];
    const fontSize = w < 540 ? 10 : 12;
    r.textOutlined(t('shop.stats'), cx, sy, '#aaa', '#000', 15, 'center', 'middle');
    r.text(t('shop.statDamage', { value: player.damage }), cols[0], sy + 22, '#ccc', fontSize, 'center');
    r.text(t('shop.statHp', { hp: player.hp, maxHp: player.maxHp }), cols[1], sy + 22, '#ccc', fontSize, 'center');
    r.text(t('shop.statSpeed', { value: player.speed }), cols[2], sy + 22, '#ccc', fontSize, 'center');
    r.text(t('shop.statRange', { value: player.attackRange }), cols[3], sy + 22, '#ccc', fontSize, 'center');
    const atkPerSec = player.attackCooldown > 0 ? (1 / player.attackCooldown) : 0;
    r.text(t('shop.statAtkSpeed', { value: atkPerSec.toFixed(1) }), cols[4], sy + 22, '#ccc', fontSize, 'center');
    if (player.lifestealPct > 0) {
      r.text(t('shop.statVamp', { value: Math.round(player.lifestealPct * 100) }), cols[0], sy + 42, '#d46a7a', fontSize, 'center');
    }
    if (player.thornsPct > 0) {
      r.text(t('shop.statThorns', { value: Math.round(player.thornsPct * 100) }), cols[1], sy + 42, '#6bbf4a', fontSize, 'center');
    }
    if (player.magnetRangeMul > 1) {
      r.text(t('shop.statMagnet', { value: player.magnetRangeMul.toFixed(1) }), cols[2], sy + 42, '#d4a020', fontSize, 'center');
    }
    if (player.fullArcAttack) {
      r.text(t('shop.statWideArc'), cols[3], sy + 42, '#e2b44a', fontSize, 'center');
    }
  }

  renderPaused(r) {
    r.setAlpha(0.6);
    r.rect(0, 0, r.width, r.height, '#000');
    r.resetAlpha();
    const cx = r.width / 2;
    const cy = r.height / 2;
    r.textOutlined(t('pause.title'), cx, cy - 20, '#ffd700', '#000', 56, 'center', 'middle');
    r.textOutlined(t('pause.resume'), cx, cy + 30, '#ccc', '#000', 18, 'center', 'middle');
    r.textOutlined(t('pause.mute'), cx, cy + 56, '#888', '#000', 14, 'center', 'middle');

    this.renderFullscreenButton(r);
  }

  // Brief between-wave stats panel. `summary` is the payload built by
  // Game._endWave (wave / caravans / damage / gold / flawless / duration).
  renderWaveSummary(r, summary) {
    if (!summary) return;
    r.setAlpha(0.72);
    r.rect(0, 0, r.width, r.height, '#000');
    r.resetAlpha();

    const cx = r.width / 2;
    const cy = r.height / 2;
    const titleSize = Math.max(28, Math.min(54, Math.floor(r.width / 22)));
    const lineSize = Math.max(14, Math.min(22, Math.floor(r.width / 50)));
    const hintSize = Math.max(11, Math.min(14, Math.floor(r.width / 80)));

    const lines = [
      {
        text: t('waveEnd.caravans', {
          robbed: summary.caravansRobbed,
          total: summary.caravansTotal,
        }),
        color: summary.caravansRobbed === summary.caravansTotal ? '#fff' : '#ccc',
      },
      {
        text: t('waveEnd.guards', { count: summary.guardsKilled }),
        color: '#ccc',
      },
      {
        text: t('waveEnd.damage', { value: summary.damageTaken }),
        color: summary.damageTaken === 0 ? '#00ff88' : '#d46a7a',
      },
      {
        text: t('waveEnd.gold', { value: summary.goldEarned }),
        color: CONST.COLOR_GOLD,
      },
    ];

    const lineGap = Math.floor(lineSize * 1.7);
    const startY = cy - Math.floor(lineGap * (lines.length - 1) / 2);

    // Title anchors to the first stat line so it never bleeds into the
    // "Корованов ограблено" row. Old formula (cy − titleSize · 1.6) was
    // tuned for the title-size / line-size ratio at desktop and broke
    // at every other viewport once both axes scaled.
    const titleGap = 16;
    const titleY = Math.max(
      titleSize / 2 + 8,                                   // viewport top clamp
      startY - Math.ceil(lineSize / 2 + titleSize / 2 + titleGap)
    );
    r.textOutlined(
      t('waveEnd.title', { wave: summary.wave }),
      cx, titleY, '#ffd700', '#000', titleSize, 'center', 'middle'
    );

    for (let i = 0; i < lines.length; i++) {
      const ln = lines[i];
      r.textOutlined(
        ln.text, cx, startY + i * lineGap, ln.color, '#000', lineSize, 'center', 'middle'
      );
    }

    if (summary.flawlessBonus > 0) {
      r.textOutlined(
        t('waveEnd.flawless', { value: summary.flawlessBonus }),
        cx, startY + lines.length * lineGap, '#00ff88', '#000', lineSize, 'center', 'middle'
      );
    }

    r.textOutlined(
      t('waveEnd.continue'),
      cx, r.height - Math.max(28, Math.floor(r.height * 0.08)),
      '#888', '#000', hintSize, 'center', 'middle'
    );
  }

  renderGameOver(r, game) {
    const cy = r.height / 2;

    r.rect(0, 0, r.width, r.height, '#1a1a2e');

    // Leaderboard panel — fetched async by Game._refreshLeaderboard().
    // Lives in the bottom-right corner; hidden until the fetch returns.
    //
    // Layout is column-based so the rank ("1.", "10.") aligns flush-left
    // and the score / wave columns stay flush-right regardless of name
    // width. Rebirth count renders as a row of ★ between name and score
    // (capped at 5 stars to keep the column width bounded; truncated
    // overflow shown as "★×N" past the cap).
    const lb = (game && Array.isArray(game.leaderboard)) ? game.leaderboard : [];
    const rightEdge = r.width - 24;
    // Column widths (in px at 13px font, monospace). Tuned for a 14-char name max.
    const colWave = 36;   // "w22"
    const colScore = 60;  // "14979"
    const colStars = 70;  // "★★★★★"
    const colName = 110;  // "PlayerName123"
    const colRank = 36;   // "10."
    const panelLeft = rightEdge - (colRank + colName + colStars + colScore + colWave);

    const bestScore = getBestScore();
    const bestWave = getBestWave();
    const lines = [
      [t('gameover.title'), 48],
      [t('gameover.diedOnWave', { wave: game.wave }), 20],
      [t('gameover.caravansRobbed', { count: game.caravansRobbed }), 20],
      [t('gameover.score', { score: game.score }), 24],
      ...(game.newRecord ? [[t('gameover.newRecord'), 22]]
        : bestScore > 0 ? [[t('gameover.record', { score: bestScore, wave: bestWave }), 16]] : []),
      [t('gameover.continue'), 16],
    ];
    const textW = Math.max(...lines.map(([str, size]) => r.measureText(str, size)));
    // The text block is centred on screen unless it would run under the
    // leaderboard — then it is centred in the space left of the panel. If
    // even that is too narrow, the panel is dropped: the run's result wins.
    const gap = 16;
    let showBoard = lb.length > 0;
    let cx = r.width / 2;
    if (showBoard && cx + textW / 2 + gap > panelLeft) {
      cx = (panelLeft - gap) / 2;
      if (cx - textW / 2 < 8) { showBoard = false; cx = r.width / 2; }
    }
    // Game._syncTelegramCta centres the DOM Telegram button on this x; the
    // rects let the layout e2e test check that nothing overlaps.
    this.gameOverTextX = cx;
    this.gameOverTextRect = { x: cx - textW / 2, y: cy - 140, w: textW, h: 280 };
    this.gameOverBoardRect = null;

    r.textOutlined(lines[0][0], cx, cy - 110, '#e74c3c', '#000', 48, 'center', 'middle');
    r.textOutlined(lines[1][0], cx, cy - 40, '#fff', '#000', 20, 'center', 'middle');
    r.textOutlined(lines[2][0], cx, cy - 10, '#fff', '#000', 20, 'center', 'middle');
    r.textOutlined(lines[3][0], cx, cy + 25, CONST.COLOR_GOLD, '#000', 24, 'center', 'middle');
    if (game.newRecord) {
      r.textOutlined(t('gameover.newRecord'), cx, cy + 65, '#00ff88', '#000', 22, 'center', 'middle');
    } else if (bestScore > 0) {
      r.textOutlined(
        t('gameover.record', { score: bestScore, wave: bestWave }),
        cx, cy + 65, '#aaa', '#000', 16, 'center', 'middle'
      );
    }

    // No continue hint while the nickname dialog holds the screen.
    const blink = Math.sin(performance.now() / 300) > 0 && !game.askingName;
    if (blink) {
      // Continue hint sits above the Telegram CTA when it is shown (DOM
      // overlay at CSS `bottom: 7vh`, ≈ 38 px tall). On short viewports
      // (landscape phone ~360 px) the fixed cy+130 anchor lands on top of
      // the CTA — clamp to leave a 12 px gap above its top edge.
      const ctaTop = r.height - Math.round(r.height * 0.07) - 38;
      const continueY = game.showTelegramCta ? Math.min(cy + 130, ctaTop - 16) : cy + 130;
      r.textOutlined(t('gameover.continue'), cx, continueY, '#aaa', '#000', 16, 'center', 'middle');
    }

    if (showBoard) {
      const rows = Math.min(10, lb.length);
      const lineH = 18;
      const headerH = 22;
      const totalH = headerH + rows * lineH;
      let lbY = Math.max(60, r.height - totalH - 24);
      this.gameOverBoardRect = { x: panelLeft, y: lbY, w: rightEdge - panelLeft, h: totalH };
      // Header keeps the existing right-aligned glyph.
      r.text(t('menu.leaderboard'), rightEdge, lbY, '#ffd700', 16, 'right');
      lbY += headerH;
      const xRank = panelLeft;
      const xName = panelLeft + colRank;
      const xStars = panelLeft + colRank + colName;
      const xScore = panelLeft + colRank + colName + colStars + colScore;
      const xWave = rightEdge;
      const STAR = '★';
      const STAR_CAP = 5;
      for (let i = 0; i < rows; i++) {
        const e = lb[i];
        const color = i === 0 ? '#fff' : '#bbb';
        const name = (e.name || '???').slice(0, 14);
        const reb = Math.max(0, e.rebirths | 0);
        const stars = reb === 0 ? '' :
          (reb <= STAR_CAP ? STAR.repeat(reb) : `${STAR}×${reb}`);
        r.text(`${i + 1}.`, xRank, lbY, color, 13, 'left');
        r.text(name, xName, lbY, color, 13, 'left');
        if (stars) r.text(stars, xStars, lbY, '#ffd700', 13, 'left');
        r.text(String(e.score), xScore, lbY, color, 13, 'right');
        r.text(`w${e.wave}`, xWave, lbY, color, 13, 'right');
        lbY += lineH;
      }
    }

    this.renderFullscreenButton(r);
  }
}
